import React, { useState } from 'react';
import Head from 'next/head';
import jsCookie from 'js-cookie';
import { getAuth, signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import styles from './LoginModal.module.scss';

import { log_error } from '../../utils/logger';
import initializeFirebase from '../../utils/initializeFirebase';
import { LoginAPIService } from '../../lib/loginHandler/LoginAPIService';
import { cleanPhoneNumber, isPhoneNumberValid } from '../../lib/phoneNumber';
import googleAPIService from '../../utils/googleAPIService';
import { SignupAPIService, IRegister } from '../../lib/loginHandler/SignupAPIService';
import KycApiService, { IKYCverification } from '../../lib/loginHandler/KycApiService';
import { LOGIN_ERROR_MESSAGE } from '../../lib/constant';
import { Alert } from '../Alert';
import Login from '../Login';

declare global {
  interface Window {
    recaptchaVerifier: any;
    confirmationResult: any;
    gapi: any;
  }
}

type AlertData = React.ComponentProps<typeof Alert>['alertList'][0];

interface ILoginModal {
  closeModalClick: () => void;
}

const LoginModal = (props: ILoginModal) => {
  const { closeModalClick } = props;

  const [loginSuccess, setLoginSuccess] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [clickedNext, setClickedNext] = useState(false);

  const [alertList, setAlertList] = useState<AlertData[]>([]); // Alert Popup for internal server error
  const [errorMessage, setErrorMessage] = useState({}); //Error msg on input validation

  const [isRegister, setIsRegister] = useState(false);
  const [isIDverification, setIsIDverification] = useState(false);
  const [isUserLocked, setUserLocked] = useState(false);

  const showValidationError = (errorType, inputField) => {
    const newErrorMessage = {
      [inputField]: LOGIN_ERROR_MESSAGE[errorType]
    };

    setErrorMessage({
      ...errorMessage,
      ...newErrorMessage,
    });
  };

  const showInternalError = () => {
    const internalErrorMsg: AlertData = {
      id: 4,
      title: 'Error',
      description: 'Terjadi kesalahan pada sistem, silakan coba beberapa saat lagi',
      type: 'error',
    };
    setAlertList([internalErrorMsg]);
  };

  const validatePhoneNumber = (phoneNum) => {
    if (!phoneNum) {
      showValidationError('NO_EMPTY_PHONE_NUMBER', 'phoneNumber');
      setClickedNext(false);
      return false;
    }
    if (!isPhoneNumberValid(phoneNum)) {
      showValidationError('INVALID_PHONE_NUMBER', 'phoneNumber');
      setClickedNext(false);
      return false;
    }
    setErrorMessage({});
    return true;
  };

  const validateEmailPassword = (payload) => {
    if (!payload.email) {
      setClickedNext(false);
      showValidationError('NO_EMPTY_EMAIL', 'email');
      return false;
    }

    if (!payload.password) {
      setClickedNext(false);
      showValidationError('NO_EMPTY_PASSWORD', 'password');
      return false;
    }

    setErrorMessage({});
    return true;
  };

  const validateRegistration = (payload: IRegister) => {
    if (!payload.fullname) {
      setClickedNext(false);
      showValidationError('NO_EMPTY_FULLNAME', 'fullname');
      return false;
    }

    if (!payload.gender) {
      setClickedNext(false);
      showValidationError('NO_EMPTY_GENDER', 'gender');
      return false;
    }
    if (!payload.password) {
      setClickedNext(false);
      showValidationError('NO_EMPTY_PASSWORD', 'password');
      return false;
    }

    if (payload.password.length < 5) {
      setClickedNext(false);
      showValidationError('MINIMUM_PASSWORD', 'password');
      return false;
    }

    if (payload.password !== payload.repassword) {
      setClickedNext(false);
      showValidationError('DISSIMILAR_REPASSWORD', 'repassword');
      return false;
    }

    setErrorMessage({});
    return validatePhoneNumber(payload.phone);
  };

  const validateIDverification = (formData: IKYCverification) => {
    if (!formData.ktp && !formData.passport) {
      setClickedNext(false);
      showValidationError('REQUIRED_TO_FILL', 'ktp');
      showValidationError('REQUIRED_TO_FILL', 'passport');
      return false;
    }

    if (formData.ktp && formData.ktp.length < 15) {
      setClickedNext(false);
      showValidationError('MINIMUM_KTP_LENGTH', 'ktp');
      return false;
    }

    if (!formData.annualIncome) {
      setClickedNext(false);
      showValidationError('REQUIRED_TO_FILL', 'annualIncome');
      return false;
    }

    if (!formData.sourceOfFund) {
      setClickedNext(false);
      showValidationError('REQUIRED_TO_FILL', 'sourceOfFund');
      return false;
    }

    return true;
  };

  const sendOtp = async (phone) => {
    const phoneValid = validatePhoneNumber(phone.value);

    if (phoneValid) {
      setClickedNext(true);
      await initializeFirebase();

      const auth = getAuth();
      window.recaptchaVerifier = new RecaptchaVerifier(
        'firebase-otp-login',
        {
          size: 'invisible',
          callback: (response) => { },
        },
        auth
      );

      try {
        const res = await signInWithPhoneNumber(auth, `+62${phone.value}`, window.recaptchaVerifier);
        window.confirmationResult = res;
        return true;
      } catch (err) {
        log_error('User OTP Sign in Error', 'error', err);
        showInternalError();
        setClickedNext(false);
        return false;
      }
    }
  };

  const verificationOtp = async (otp) => {
    const res = await window.confirmationResult.confirm(otp);
    const requestObject = {
      type: 'firebaseLogin',
      firebaseLogin: {
        uid: res.user.uid,
      },
    };
    const uidLoginSuccess = await LoginAPIService(requestObject);
    if (uidLoginSuccess) {
      setLoginSuccess(true);
      setTimeout(() => window.location.reload(), 1000);
    } else {
      showInternalError();
      setClickedNext(false);
    }
  };

  const loginWithEmail = async (payload) => {
    const payloadIsValid = validateEmailPassword(payload);
    if (payloadIsValid) {
      setClickedNext(true);
      const requestObject = {
        type: 'emailLogin',
        emailLogin: payload,
      };

      try {
        const token = await LoginAPIService(requestObject);
        if (token) {
          setLoginSuccess(true);
          setTimeout(() => window.location.reload(), 1000);
        }

      } catch (error) {
        setClickedNext(false);
        if (error.message && error.message.includes('Tidak ditemukan akun aktif')) {
          setUserLocked(true);
          setTimeout(() => {
            setUserLocked(false);
          }, 5000);
    
          return;
        }
    
        showValidationError('UNREGISTERED_PASSWORD_OR_EMAIL', 'password');
      }
      const token = await LoginAPIService(requestObject);
      if (token) {
        setLoginSuccess(true);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        setClickedNext(false);
        // TODO: get locked user error me
          showValidationError('UNREGISTERED_PASSWORD_OR_EMAIL', 'password');
      }
    }
  };

  const loginWithGoogle = async () => {
    let auth2 = await googleAPIService();
    if (window && window.gapi) {
      setClickedNext(true);
      setGoogleLoading(true);
      try {
        const res = await auth2.grantOfflineAccess({ prompt: 'select_account' });
        if (res && res.code) {
          const requestObject = {
            type: 'googleLogin',
            googleLogin: {
              authCode: res.code,
              redirectUri: window.location.origin,
            },
          };
          const googleLoginSuccess = await LoginAPIService(requestObject);
          if (googleLoginSuccess) {
            setLoginSuccess(true);
            setTimeout(() => window.location.reload(), 1000);
          } else {
            showInternalError();
            setClickedNext(false);
          }
        }
      } catch (err) {
        log_error('Google Login error', 'error', err);
        showInternalError();
        setClickedNext(false);
      }
    }
  };

  const LoginSuccessPopup = () => {
    return (
      <div className={styles['otp__success']}>
        <div className={styles['otp__success--box']}>
          <div className={styles['otp__success--icon']}>&#10003;</div>
          <h2 className="">Anda telah berhasil login</h2>
        </div>
      </div>
    );
  };

  const goToRegister = () => {
    setIsRegister(true);
  };

  const goToForgetPassword = () => {
    window.location.href = '/account/forgot-password/';
  };

  const createNewUser = async (payload: IRegister) => {
    const isPayloadValid = validateRegistration(payload);

    if (isPayloadValid) {
      setErrorMessage({});
      const registerResult = await SignupAPIService(payload);

      if (registerResult.status === 'fail') {
        setErrorMessage({
          ...errorMessage,
          phoneNumber: registerResult.phoneNumber ? registerResult.phoneNumber[0] : LOGIN_ERROR_MESSAGE.INVALID_PHONE_NUMBER,
        });
        return;
      } else {
        const requestObject = {
          type: 'emailLogin',
          emailLogin: {
            email: registerResult.phoneNumber,
            password: registerResult.password,
          },
        };

        const token = await LoginAPIService(requestObject);

        if (token) {
          const savePersonPayload = {
            token:  token.data?.login?.access,
            fullName: payload.fullname,
            gender: payload.gender,
            dob: `${payload.date}-${payload.month}-${payload.year}`,
            phoneNumber: cleanPhoneNumber(payload.phone, ''),
            mainProfile: true,
            isUpdateUser: true,
            userId: registerResult?.data?.id,
          };

          const result = await KycApiService(savePersonPayload, 'adduser');
          jsCookie.set('personID', result.id);
          setIsRegister(false);
          setIsIDverification(true);
        } else {
          showInternalError();
          setTimeout(() => window.location.reload(), 1000);
        }
      }
    }
  };

  const handleIDverification = async (payloadObj) => {
    const isValid = validateIDverification(payloadObj);
    if (isValid) {
      await KycApiService(payloadObj, 'updateuser');
      setLoginSuccess(true);
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  return (
    <div>
      <Head>
        <script
          src="https://apis.google.com/js/client:platform.js"
          id="google-sign-in-server-side"
          async
        />
      </Head>
      {loginSuccess ? (
        <LoginSuccessPopup />
      ) : (
        <Login
          title="Selamat Datang di Lifepal"
          isModal={true}
          isRegister={isRegister}
          isIDverification={isIDverification}
          onPageChange={(page) => {
            setClickedNext(false);
            setErrorMessage({});
            setIsRegister(false);
            return page;
          }}
          emailIconSrc="/media/static/images/icons/mail.svg"
          googleIconSrc="/media/static/images/icons/google-icon.svg"
          goToRegister={goToRegister}
          goToForgetPassword={goToForgetPassword}
          loginWithEmail={loginWithEmail}
          timeLeft={60}
          errorMessage={errorMessage}
          loginWithGoogle={loginWithGoogle}
          googleButtonLoading={googleLoading}
          nextButtonLoading={clickedNext}
          closeModalClick={closeModalClick}
          handleRegister={createNewUser}
          handleIDverification={handleIDverification}
          isUserLocked={isUserLocked}
        />
      )}
      <div id="firebase-otp-login" style={{ display: 'none' }} />{' '}
      {alertList.length > 0 && (
        <Alert
          alertList={alertList}
          position="top-right"
          alertClose={() => setAlertList([])}
          autoClose={true}
          autoCloseTime={1500}
        />
      )}
    </div>
  );
};

export default LoginModal;
